/**
 * Configuration bootstrap.
 *
 * `GET /config` carries the Stripe publishable key, the identifiers the sign-in
 * form needs, and the bid constants the pricing copy is written from — so those
 * numbers are never duplicated in the bundle. Public browsing does not depend
 * on it: a leaderboard renders whether or not the call has returned.
 */

import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';

import type { PublicConfigResponse } from '@bidtohire/shared';

import { apiRequest } from './api';

interface ConfigState {
  readonly config: PublicConfigResponse | undefined;
  readonly isLoading: boolean;
  readonly failed: boolean;
}

const ConfigContext = createContext<ConfigState>({
  config: undefined,
  isLoading: true,
  failed: false,
});

export function ConfigProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const query = useQuery({
    queryKey: ['config'],
    queryFn: () => apiRequest<PublicConfigResponse>('/config'),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const value: ConfigState = {
    config: query.data,
    isLoading: query.isLoading,
    failed: query.isError,
  };

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfigState(): ConfigState {
  return useContext(ConfigContext);
}

export function useConfig(): PublicConfigResponse | undefined {
  return useContext(ConfigContext).config;
}

/** Where uploaded logos are served from; `/assets-cdn` in every environment. */
export function useAssetsBasePath(): string {
  return useConfig()?.assetsBasePath ?? '/assets-cdn';
}
